import type { RegisterOption } from './registerOptions';
import { isBusinessClientLogin } from '../auth/userLoginType';
import { resolveApiToken } from '../api/withApiAuth';

const BUSINESS_WORKER_REGISTER_OPTION = 'business worker' as const satisfies RegisterOption;

export const REGISTER_WIRE_AUTH_OMIT_BODY_KEYS = [
  'accessToken',
  'access_token',
  'refreshToken',
  'refresh_token',
  'token',
  'id_token',
  'google_id_token',
  'credential',
] as const;

export function registerSubmitNeedsSessionAuth(
  option: RegisterOption,
  userLoginType: string | null,
  privateClientCompletionMode: boolean,
  isLoggedIn: boolean,
): boolean {
  if (!isLoggedIn) {
    return false;
  }
  if (privateClientCompletionMode && option === 'private client') {
    return true;
  }
  if (option === BUSINESS_WORKER_REGISTER_OPTION && isBusinessClientLogin(userLoginType)) {
    return true;
  }
  return false;
}

export function stripRegisterWireAuthFields(body: Record<string, unknown>): void {
  for (const key of REGISTER_WIRE_AUTH_OMIT_BODY_KEYS) {
    delete body[key];
  }
}

export function buildRegisterSubmitHeaders(
  option: RegisterOption,
  userLoginType: string | null,
  privateClientCompletionMode: boolean,
  isLoggedIn: boolean,
  bearerToken?: string | null,
): Headers {
  const headers = new Headers({
    Accept: 'application/json, text/plain, */*',
    'Content-Type': 'application/json',
  });
  if (!registerSubmitNeedsSessionAuth(option, userLoginType, privateClientCompletionMode, isLoggedIn)) {
    return headers;
  }
  const token = bearerToken?.trim();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

/** Refresh access JWT before session-backed register (business worker under business client). */
export async function buildRegisterSubmitHeadersAsync(
  option: RegisterOption,
  userLoginType: string | null,
  privateClientCompletionMode: boolean,
  isLoggedIn: boolean,
): Promise<Headers> {
  const needsSession = registerSubmitNeedsSessionAuth(
    option,
    userLoginType,
    privateClientCompletionMode,
    isLoggedIn,
  );
  const freshToken = needsSession ? await resolveApiToken() : null;
  return buildRegisterSubmitHeaders(
    option,
    userLoginType,
    privateClientCompletionMode,
    isLoggedIn,
    freshToken,
  );
}

export function buildRegisterPostInit(
  headers: Headers,
  bodyJson: string,
  option: RegisterOption,
  userLoginType: string | null,
  privateClientCompletionMode: boolean,
  isLoggedIn: boolean,
): RequestInit {
  const anonymous = !registerSubmitNeedsSessionAuth(
    option,
    userLoginType,
    privateClientCompletionMode,
    isLoggedIn,
  );
  const wireHeaders = new Headers(headers);
  if (anonymous) {
    wireHeaders.delete('Authorization');
  }
  return {
    method: 'POST',
    headers: wireHeaders,
    body: bodyJson,
    // Never send httpOnly refresh cookies on register — Bearer only when session-backed.
    credentials: 'omit',
    cache: 'no-store',
  };
}
